/**
 * AddLabelDialog - Dialog for adding new labels to Text.parameters.labels
 *
 * Combines name input with LabelBlock style fields for complete label creation.
 *
 * Sprint: screen-editor-body
 * Task: Phase 3 - AddLabelDialog
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Divider,
} from '@mui/material';
import type { TemplateLabelBlock } from '@modules/reactFlow/types/templateLabelBlock.types';
import { LabelBlockEditor } from './LabelBlockEditor';

/**
 * Default LabelBlock values for new labels
 */
const DEFAULT_LABEL_BLOCK: Partial<TemplateLabelBlock> = {
  fontFamily: 'SF Pro',
  fontSize: 18,
  fontWeight: 400,
  lineHeight: 32,
  color: '#000000',
  backgroundColor: '#ffffff',
  textAlign: 'center',
};

/**
 * Props for AddLabelDialog
 */
export interface AddLabelDialogProps {
  /** Whether dialog is open */
  open: boolean;

  /** Callback to close dialog */
  onClose: () => void;

  /** Callback when label is added/edited - receives name and LabelBlock */
  onAdd: (name: string, labelBlock: Partial<TemplateLabelBlock>) => void;

  /** Existing label keys to prevent duplicates */
  existingKeys: string[];

  /** Initial name value (e.g., selected text for pre-fill) */
  initialName?: string;

  /** Initial LabelBlock styles (inherit from parent Text element or for edit pre-fill) */
  initialLabelBlock?: Partial<TemplateLabelBlock>;

  /** Dialog mode: 'add' or 'edit' (default: 'add') */
  mode?: 'add' | 'edit';

  /** Key being edited (required when mode='edit') */
  editingKey?: string;
}

/**
 * AddLabelDialog - dialog for adding labels with name + styles
 *
 * Layout:
 * - Name input at top
 * - LabelBlock style fields in 2-column grid
 * - Cancel / Add buttons
 */
export const AddLabelDialog: React.FC<AddLabelDialogProps> = ({
  open,
  onClose,
  onAdd,
  existingKeys,
  initialName = '',
  initialLabelBlock,
  mode = 'add',
  editingKey,
}) => {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [labelBlock, setLabelBlock] = useState<Partial<TemplateLabelBlock>>(DEFAULT_LABEL_BLOCK);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      // In edit mode, use editingKey as name; otherwise use initialName
      const nameValue = mode === 'edit' && editingKey ? editingKey : initialName;
      setName(nameValue);
      setError(null);
      // Use initialLabelBlock if provided (inherit from parent Text or edit pre-fill), otherwise defaults
      setLabelBlock(initialLabelBlock || DEFAULT_LABEL_BLOCK);
    } else {
      // Reset when dialog closes
      setName('');
      setError(null);
      setLabelBlock(DEFAULT_LABEL_BLOCK);
    }
  }, [open, initialName, initialLabelBlock, mode, editingKey]);

  // Validate name
  const validateName = useCallback(
    (value: string): string | null => {
      const trimmed = value.trim();

      if (!trimmed) {
        return null; // Empty is not an error, just prevents submission
      }

      // In edit mode, allow keeping the original name (exclude editingKey from check)
      const keysToCheck = mode === 'edit' && editingKey
        ? existingKeys.filter((k) => k !== editingKey)
        : existingKeys;

      if (keysToCheck.includes(trimmed)) {
        return `"${trimmed}" already exists`;
      }

      return null;
    },
    [existingKeys, mode, editingKey]
  );

  // Handle name change
  const handleNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setName(value);
      setError(validateName(value));
    },
    [validateName]
  );

  // Handle LabelBlock change
  const handleLabelBlockChange = useCallback((newValue: Partial<TemplateLabelBlock>) => {
    setLabelBlock(newValue);
  }, []);

  // Check if form is valid
  const isValid = name.trim().length > 0 && !error;

  // Handle submit
  const handleSubmit = useCallback(() => {
    if (!isValid) return;

    const trimmedName = name.trim();
    onAdd(trimmedName, labelBlock);
    onClose();
  }, [isValid, name, labelBlock, onAdd, onClose]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // Handle Enter key on name input
  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && isValid) {
        event.preventDefault();
        handleSubmit();
      }
    },
    [isValid, handleSubmit]
  );

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>{mode === 'edit' ? 'Edit Label' : 'Add Label'}</DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {/* Name Input */}
          <TextField
            autoFocus
            fullWidth
            label="Label Name"
            placeholder="e.g., highlight, bold, price..."
            value={name}
            onChange={handleNameChange}
            onKeyPress={handleKeyPress}
            error={!!error}
            helperText={error}
            size="small"
            sx={{ mb: 2 }}
            data-testid="label-name-input"
            inputProps={{
              'aria-label': 'Label Name',
            }}
          />

          <Divider sx={{ mb: 2 }} />

          {/* LabelBlock Style Fields */}
          <LabelBlockEditor
            value={labelBlock}
            onChange={handleLabelBlockChange}
            includePadding={false}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!isValid} data-testid="label-submit-btn">
          {mode === 'edit' ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddLabelDialog;
